import { useScopeStore } from "@/stores/scope";
import { useAuthStore } from "@/stores/auth";
import { useEffect, useState } from "react";

export const COLLECTION_THRESHOLD = 5;
export function collectionPage<T>(
  items: readonly T[],
  options: {
    query: string;
    filter: string;
    page: number;
    pageSize: number;
    enabled?: boolean;
    text: (item: T) => string;
    matches?: (item: T, filter: string) => boolean;
    predicate?: (item: T) => boolean;
  },
) {
  // Input MUST be a complete authorized array, never one server page.
  const controls =
    options.enabled !== false && items.length > COLLECTION_THRESHOLD;
  const query = controls ? options.query.trim().toLocaleLowerCase() : "";
  const filtered = controls
    ? items.filter(
        (item) =>
          (!query || options.text(item).toLocaleLowerCase().includes(query)) &&
          (!options.filter ||
            !options.matches ||
            options.matches(item, options.filter)) &&
          (!options.predicate || options.predicate(item)),
      )
    : [...items];
  const pageSize = Math.max(1, options.pageSize);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const page = controls ? Math.max(1, Math.min(options.page, pages)) : 1;
  return {
    controls,
    total: items.length,
    filteredTotal: filtered.length,
    page,
    pages,
    items: controls
      ? filtered.slice((page - 1) * pageSize, page * pageSize)
      : filtered,
  };
}

export function useCollection<T>(
  items: readonly T[],
  options: {
    scope: string;
    text: (item: T) => string;
    matches?: (item: T, filter: string) => boolean;
    predicate?: (item: T) => boolean;
  },
) {
  const team = useScopeStore((s) => s.teamId);
  const season = useScopeStore((s) => s.seasonId);
  const viewer = useAuthStore((s) => s.me?.user?.id);
  options = {
    ...options,
    scope: `${viewer ?? "anonymous"}:${team ?? ""}:${season ?? ""}:${options.scope}`,
  };
  const [state, setState] = useState({
    scope: options.scope,
    query: "",
    filter: "",
    page: 1,
    pageSize: 5,
  });
  const current =
    state.scope === options.scope
      ? state
      : { scope: options.scope, query: "", filter: "", page: 1, pageSize: 5 };
  const result = collectionPage(items, {
    ...current,
    ...options,
  });
  useEffect(() => {
    if (
      state.scope !== options.scope ||
      (items.length <= COLLECTION_THRESHOLD &&
        (state.query || state.filter || state.page !== 1))
    ) {
      setState({
        scope: options.scope,
        query: "",
        filter: "",
        page: 1,
        pageSize: state.pageSize,
      });
    }
  }, [items.length, options.scope, state]);
  return {
    ...result,
    query: result.controls ? current.query : "",
    filter: result.controls ? current.filter : "",
    pageSize: current.pageSize,
    reveal: (matches: (item: T) => boolean, filter = "") => {
      const visible = items.filter(
        (item) => !filter || !options.matches || options.matches(item, filter),
      );
      const index = visible.findIndex(matches);
      setState({
        ...current,
        query: "",
        filter,
        page: Math.max(1, Math.floor(index / current.pageSize) + 1),
      });
    },
    clear: () => setState({ ...current, query: "", filter: "", page: 1 }),
    setQuery: (query: string) => setState({ ...current, query, page: 1 }),
    setFilter: (filter: string) => setState({ ...current, filter, page: 1 }),
    setPage: (page: number) => setState({ ...current, page }),
    setPageSize: (pageSize: number) =>
      setState({ ...current, pageSize, page: 1 }),
  };
}
